import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Paperclip, Upload, Link2, Trash2, Download, ExternalLink, File, Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { sprintService } from '@/services/sprint.service';
import type { Attachment } from '@/services/task.service';

interface AttachmentsDialogProps {
  entityType: string;
  entityId: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AttachmentsDialog({
  entityType, entityId, title, open, onOpenChange,
}: AttachmentsDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [urlName, setUrlName] = useState('');

  const queryKey = ['attachments', entityType, entityId];

  const { data: attachments = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => sprintService.listAttachments(entityType, entityId).then((r) => r.data.data),
    enabled: open && !!entityId,
  });

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['attachments-count', entityType, entityId] });
  }

  const uploadMut = useMutation({
    mutationFn: (file: File) => sprintService.uploadAttachment(entityType, entityId, file),
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'File uploaded' });
    },
    onError: (e: any) => {
      const msg = e.response?.data?.error?.message || e.message || 'Failed';
      toast({ title: 'Upload failed', description: msg, variant: 'destructive' });
    },
  });

  const addUrlMut = useMutation({
    mutationFn: () => {
      let normalizedUrl = url.trim();
      if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl)) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      return sprintService.addAttachmentUrl(entityType, entityId, normalizedUrl, urlName.trim() || undefined);
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Link added' });
      setUrl('');
      setUrlName('');
    },
    onError: (e: any) => {
      const msg = e.response?.data?.error?.message || e.message || 'Failed';
      toast({ title: 'Failed to add link', description: msg, variant: 'destructive' });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (attId: string) => sprintService.deleteAttachment(attId),
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Attachment removed' });
    },
    onError: () => toast({ title: 'Delete failed', variant: 'destructive' }),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      uploadMut.mutate(file);
      e.target.value = '';
    }
  }

  async function handleDownload(att: Attachment) {
    try {
      if (att.attachment_type === 'url') {
        window.open(att.url || '', '_blank');
        return;
      }
      // File: API proxies the binary stream from MinIO
      const res = await sprintService.downloadAttachment(att.id);
      const blob = res.data as Blob;
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.file_name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  }

  function formatFileSize(bytes: number | null): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            Attachments
          </DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="gap-1">
              <Upload className="h-3.5 w-3.5" /> Upload File
            </TabsTrigger>
            <TabsTrigger value="link" className="gap-1">
              <Link2 className="h-3.5 w-3.5" /> Add Link
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-3">
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-brand-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadMut.isPending ? (
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
              )}
              <p className="text-sm text-muted-foreground mt-2">
                {uploadMut.isPending ? 'Uploading...' : 'Drag & drop files here, or click to select'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Max 10MB per file</p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                disabled={uploadMut.isPending}
              />
            </div>
          </TabsContent>

          <TabsContent value="link" className="mt-3 space-y-3">
            <div className="space-y-2">
              <Label>URL *</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Label (optional)</Label>
              <Input
                value={urlName}
                onChange={(e) => setUrlName(e.target.value)}
                placeholder="Link description"
              />
            </div>
            <Button
              size="sm"
              onClick={() => addUrlMut.mutate()}
              disabled={!url.trim() || addUrlMut.isPending}
            >
              {addUrlMut.isPending ? 'Adding...' : 'Add Link'}
            </Button>
          </TabsContent>
        </Tabs>

        {/* Attachments list */}
        <div className="mt-4 space-y-2 max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
          ) : attachments.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <File className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No attachments or links yet</p>
            </div>
          ) : (
            attachments.map((att) => (
              <div
                key={att.id}
                className="flex items-center gap-3 p-2.5 rounded-lg border bg-muted/30 text-sm group"
              >
                {att.attachment_type === 'url' ? (
                  <Link2 className="h-4 w-4 text-blue-500 shrink-0" />
                ) : (
                  <File className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{att.file_name || att.url || 'Untitled'}</p>
                  <p className="text-xs text-muted-foreground">
                    {att.attachment_type === 'file' && att.file_size && formatFileSize(att.file_size)}
                    {att.attachment_type === 'url' && att.url && (
                      <span className="truncate">{att.url}</span>
                    )}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100"
                  onClick={() => handleDownload(att)}
                >
                  {att.attachment_type === 'url' ? (
                    <ExternalLink className="h-3.5 w-3.5" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-destructive opacity-0 group-hover:opacity-100"
                  onClick={() => deleteMut.mutate(att.id)}
                  disabled={deleteMut.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inline button to open attachments dialog with count badge
interface AttachmentsButtonProps {
  entityType: string;
  entityId: string;
  title: string;
}

export function AttachmentsButton({ entityType, entityId, title }: AttachmentsButtonProps) {
  const [open, setOpen] = useState(false);

  const { data: count = 0 } = useQuery({
    queryKey: ['attachments-count', entityType, entityId],
    queryFn: () =>
      sprintService.listAttachments(entityType, entityId).then((r) => r.data.data.length),
    enabled: !!entityId,
  });

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 relative"
        onClick={() => setOpen(true)}
      >
        <Paperclip className="h-4 w-4" />
        {count > 0 && (
          <Badge
            variant="secondary"
            className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[9px] leading-none"
          >
            {count}
          </Badge>
        )}
      </Button>
      <AttachmentsDialog
        open={open}
        onOpenChange={setOpen}
        entityType={entityType}
        entityId={entityId}
        title={title}
      />
    </>
  );
}
